import { create } from 'zustand';
import {
  getLibraryContent,
  getRecentUploads,
  getCategories,
  getContentDetails,
  ContentItem,
  RecentUpload,
  Category,
  ContentDetails,
} from '@/services/PagesServices/ContentManagementService';

interface ContentManagementState {
  libraryContent: ContentItem[] | null;
  recentUploads: RecentUpload[] | null;
  categories: Category[] | null;
  contentDetails: ContentDetails | null;
  loading: boolean;
  error: string | null;

  fetchLibraryContent: () => Promise<void>;
  fetchRecentUploads: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchContentDetails: (contentId: string) => Promise<void>;
}

const useContentManagementStore = create<ContentManagementState>((set, get) => ({
  libraryContent: null,
  recentUploads: null,
  categories: null,
  contentDetails: null,
  loading: false,
  error: null,

  fetchLibraryContent: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getLibraryContent();
      set({ libraryContent: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRecentUploads: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getRecentUploads();
      set({ recentUploads: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCategories: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCategories();
      set({ categories: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchContentDetails: async (contentId: string) => {
    try {
      set({ loading: true, error: null, contentDetails: null });
      const response = await getContentDetails(contentId);
      if (response.success && response.data) {
        set({ contentDetails: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load content details', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },
}));

export default useContentManagementStore;
